import { useEffect, useRef } from "react";
import { trackEventImpression } from "@/lib/eventEngagement";

/**
 * Fires an impression beacon the first time an event card actually scrolls into view (≥50%
 * visible), then disconnects -- true viewport visibility, not just "rendered in the DOM", and
 * never refires on re-render since `firedRef` persists for the card's lifetime. Same
 * IntersectionObserver shape as useInfiniteScrollSentinel.js, attach the returned ref to the
 * card's root element.
 */
export function useEventImpression(eventId) {
  const cardRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || !eventId) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !firedRef.current) {
          firedRef.current = true;
          trackEventImpression(eventId);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eventId]);

  return cardRef;
}
